import React from 'react';
import { DiffStats, SectionDiff } from '@hooks/useDiff';
import { BarChart3, Plus, Minus, MoveHorizontal } from 'lucide-react';
import { Badge } from '../ui/Badge';

interface CompareSummaryProps {
  stats: DiffStats | null;
  sections: SectionDiff[];
  onSectionClick?: (sectionName: string) => void;
}

export function CompareSummary({ stats, sections }: CompareSummaryProps): React.ReactElement | null {
  if (!stats) return null;

  return (
    <div className="flex items-center gap-8 px-8 py-4 bg-slate-950 border-t border-slate-800 text-[11px] font-black uppercase tracking-widest">
      <div className="flex items-center gap-3 text-slate-400">
        <BarChart3 size={14} className="text-accent" />
        <span>Diff Analysis</span>
      </div>

      <div className="flex items-center gap-6">
        <Badge
          variant="success-muted"
          size="xs"
          leftIcon={<Plus size={10} />}
        >
          {stats.additions} Additions
        </Badge>
        <Badge
          variant="danger-muted"
          size="xs"
          leftIcon={<Minus size={10} />}
        >
          {stats.removals} Removals
        </Badge>
        <Badge
          variant="warning-muted"
          size="xs"
          leftIcon={<MoveHorizontal size={10} />}
        >
          {stats.modifications} Changes
        </Badge>
      </div>

      <div className="h-4 w-px bg-slate-800" />

      <div className="flex items-center gap-3">
        <span className="text-slate-400">Affected Sections:</span>
        <div className="flex gap-1.5">
          {sections.map((s, i) => (
            <Badge
              key={i}
              variant={s.hasChanges ? 'primary' : 'default-muted'}
              size="xs"
              title={s.name}
            >
              {s.name.substring(0, 1)}
            </Badge>
          ))}
        </div>
      </div>

      <div className="ml-auto text-slate-400 font-mono italic">
        {stats.totalChanges} total operations mapped
      </div>
    </div>
  );
}

export default CompareSummary;
